import { useState, useEffect } from 'react';
import { Save, RefreshCw, ChevronDown, ChevronUp, FileText, Image, Type, X, Upload, FolderOpen, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Image Picker Component for CMS
const CMSImagePicker = ({ currentImage, onSelect, onClose, token }) => {
  const [images, setImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchImages();
  }, []);

  const fetchImages = async () => {
    try {
      const response = await axios.get(`${API}/upload/images`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setImages(response.data.images.map(img => img.url));
    } catch (error) {
      console.error('Error fetching images:', error);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    try {
      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      onSelect(response.data.url);
      onClose();
      toast.success('Image uploaded');
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const filteredImages = images.filter(img => 
    img.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-background w-full max-w-3xl mx-4 max-h-[80vh] overflow-hidden flex flex-col">
        <div className="p-4 border-b flex justify-between items-center">
          <h3 className="font-medium">Select Image</h3>
          <button onClick={onClose} className="p-2 hover:bg-secondary rounded-full">
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Upload section */}
        <div className="p-4 border-b bg-gray-50">
          <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed cursor-pointer hover:border-primary transition-colors">
            <Upload className="w-5 h-5" />
            <span className="text-sm font-medium">{uploading ? 'Uploading...' : 'Upload new image'}</span>
            <input type="file" className="hidden" accept="image/*" onChange={handleUpload} disabled={uploading} />
          </label>
        </div>

        {/* Search */}
        <div className="p-4 border-b">
          <Input
            placeholder="Search images..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="rounded-none"
          />
        </div>

        {/* Image grid */}
        <div className="flex-1 overflow-y-auto p-4 grid grid-cols-4 gap-3">
          {filteredImages.map((img, i) => (
            <div
              key={i}
              onClick={() => { onSelect(img); onClose(); }}
              className={`relative aspect-square cursor-pointer border-2 transition-all ${
                currentImage === img 
                  ? 'border-primary ring-2 ring-primary/20' 
                  : 'border-transparent hover:border-gray-300'
              }`}
            >
              <img src={img} alt="" className="w-full h-full object-cover" />
              {currentImage === img && (
                <div className="absolute top-1 right-1 w-6 h-6 bg-primary text-white flex items-center justify-center">
                  <Check className="w-4 h-4" />
                </div>
              )}
            </div>
          ))}
          {filteredImages.length === 0 && (
            <div className="col-span-4 py-8 text-center text-muted-foreground">
              No images found. Upload your first image above.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Define editable sections for each page
const PAGE_STRUCTURE = {
  homepage: {
    name: 'Homepage',
    sections: [
      {
        id: 'hero',
        name: 'Hero Section',
        fields: [
          { key: 'tagline', label: 'Tagline', type: 'text', placeholder: 'Embrace Giving, Embody Ownership' },
          { key: 'headline', label: 'Headline', type: 'text', placeholder: 'Where Every Thread Tells a Story' },
          { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Handcrafted luxury scarves...' },
          { key: 'hero_image', label: 'Hero Image', type: 'image', placeholder: '/images/products/desert_sunset_flying.jpg' },
        ]
      },
      {
        id: 'story',
        name: 'Brand Story Section',
        fields: [
          { key: 'caption', label: 'Caption', type: 'text', placeholder: 'The Chaleur Story' },
          { key: 'title', label: 'Title', type: 'text', placeholder: 'Born from Warmth, Crafted with Purpose' },
          { key: 'description', label: 'Description', type: 'textarea', placeholder: 'Chaleur embodies the essence...' },
          { key: 'image', label: 'Story Image', type: 'image', placeholder: '/images/products/artisan_loom_cream.jpg' },
        ]
      },
      {
        id: 'promise',
        name: 'The Promise Section',
        fields: [
          { key: 'caption', label: 'Caption', type: 'text', placeholder: 'The Chaleur Promise' },
          { key: 'title', label: 'Title', type: 'text', placeholder: 'What Sets Us Apart' },
          { key: 'feature1_title', label: 'Feature 1 Title', type: 'text', placeholder: 'Authentic Craftsmanship' },
          { key: 'feature1_desc', label: 'Feature 1 Description', type: 'textarea' },
          { key: 'feature2_title', label: 'Feature 2 Title', type: 'text', placeholder: 'Meaningful Impact' },
          { key: 'feature2_desc', label: 'Feature 2 Description', type: 'textarea' },
          { key: 'feature3_title', label: 'Feature 3 Title', type: 'text', placeholder: 'Gifting with Purpose' },
          { key: 'feature3_desc', label: 'Feature 3 Description', type: 'textarea' },
        ]
      }
    ]
  },
  about: {
    name: 'About Page',
    sections: [
      {
        id: 'hero',
        name: 'Hero Section',
        fields: [
          { key: 'title', label: 'Title', type: 'text', placeholder: 'Our Story' },
          { key: 'subtitle', label: 'Subtitle', type: 'text', placeholder: 'Where warmth meets purpose' },
          { key: 'hero_image', label: 'Hero Image', type: 'image' },
        ]
      },
      {
        id: 'story',
        name: 'Story Section',
        fields: [
          { key: 'title', label: 'Title', type: 'text', placeholder: 'The Chaleur Journey' },
          { key: 'content', label: 'Content', type: 'textarea', placeholder: 'Our story begins...' },
          { key: 'image', label: 'Story Image', type: 'image' },
        ]
      },
      {
        id: 'values',
        name: 'Values Section',
        fields: [
          { key: 'title', label: 'Title', type: 'text', placeholder: 'Our Values' },
          { key: 'value1', label: 'Value 1', type: 'text', placeholder: 'Generosity' },
          { key: 'value2', label: 'Value 2', type: 'text', placeholder: 'Kindness' },
          { key: 'value3', label: 'Value 3', type: 'text', placeholder: 'Craftsmanship' },
          { key: 'value4', label: 'Value 4', type: 'text', placeholder: 'Sustainability' },
        ]
      }
    ]
  },
  craft: {
    name: 'The Craft Page',
    sections: [
      {
        id: 'hero',
        name: 'Hero Section',
        fields: [
          { key: 'caption', label: 'Caption', type: 'text', placeholder: 'The Art of Creation' },
          { key: 'title', label: 'Title', type: 'text', placeholder: 'Handwoven Heritage' },
          { key: 'description', label: 'Description', type: 'textarea' },
          { key: 'hero_image', label: 'Hero Image', type: 'image' },
        ]
      },
      {
        id: 'techniques',
        name: 'Techniques Section',
        fields: [
          { key: 'title', label: 'Section Title', type: 'text', placeholder: 'Our Techniques' },
          { key: 'tech1_name', label: 'Technique 1 Name', type: 'text' },
          { key: 'tech1_desc', label: 'Technique 1 Description', type: 'textarea' },
          { key: 'tech1_image', label: 'Technique 1 Image', type: 'image' },
          { key: 'tech2_name', label: 'Technique 2 Name', type: 'text' },
          { key: 'tech2_desc', label: 'Technique 2 Description', type: 'textarea' },
          { key: 'tech2_image', label: 'Technique 2 Image', type: 'image' },
          { key: 'tech3_name', label: 'Technique 3 Name', type: 'text' },
          { key: 'tech3_desc', label: 'Technique 3 Description', type: 'textarea' },
          { key: 'tech3_image', label: 'Technique 3 Image', type: 'image' },
        ]
      }
    ]
  },
  impact: {
    name: 'Impact Page',
    sections: [
      {
        id: 'hero',
        name: 'Hero Section',
        fields: [
          { key: 'caption', label: 'Caption', type: 'text', placeholder: 'The Chaleur Guild' },
          { key: 'title', label: 'Title', type: 'text', placeholder: 'Impact Beyond Fashion' },
          { key: 'description', label: 'Description', type: 'textarea' },
          { key: 'hero_image', label: 'Hero Image', type: 'image' },
        ]
      },
      {
        id: 'stats',
        name: 'Impact Stats',
        fields: [
          { key: 'artisans_label', label: 'Artisans Label', type: 'text', placeholder: 'Artisans Supported' },
          { key: 'artisans_value', label: 'Artisans Count', type: 'text', placeholder: '200+' },
          { key: 'families_label', label: 'Families Label', type: 'text', placeholder: 'Families Impacted' },
          { key: 'families_value', label: 'Families Count', type: 'text', placeholder: '500+' },
          { key: 'materials_label', label: 'Materials Label', type: 'text', placeholder: 'Sustainable Materials' },
          { key: 'materials_value', label: 'Materials Value', type: 'text', placeholder: '5000kg' },
          { key: 'carbon_label', label: 'Carbon Label', type: 'text', placeholder: 'Carbon Offset' },
          { key: 'carbon_value', label: 'Carbon Value', type: 'text', placeholder: '50 tons' },
        ]
      },
      {
        id: 'stories',
        name: 'Impact Stories',
        fields: [
          { key: 'story1_title', label: 'Story 1 Title', type: 'text' },
          { key: 'story1_content', label: 'Story 1 Content', type: 'textarea' },
          { key: 'story1_image', label: 'Story 1 Image', type: 'image' },
          { key: 'story2_title', label: 'Story 2 Title', type: 'text' },
          { key: 'story2_content', label: 'Story 2 Content', type: 'textarea' },
          { key: 'story2_image', label: 'Story 2 Image', type: 'image' },
        ]
      }
    ]
  },
  club: {
    name: 'Chaleur Club Page',
    sections: [
      {
        id: 'hero',
        name: 'Hero Section',
        fields: [
          { key: 'title', label: 'Title', type: 'text', placeholder: 'Join the Chaleur Club' },
          { key: 'subtitle', label: 'Subtitle', type: 'text', placeholder: 'Exclusive rewards for our valued members' },
          { key: 'hero_image', label: 'Hero Image', type: 'image' },
        ]
      },
      {
        id: 'tiers',
        name: 'Membership Tiers',
        fields: [
          { key: 'bronze_title', label: 'Bronze Tier Title', type: 'text', placeholder: 'Bronze' },
          { key: 'bronze_benefits', label: 'Bronze Benefits', type: 'textarea' },
          { key: 'silver_title', label: 'Silver Tier Title', type: 'text', placeholder: 'Silver' },
          { key: 'silver_benefits', label: 'Silver Benefits', type: 'textarea' },
          { key: 'gold_title', label: 'Gold Tier Title', type: 'text', placeholder: 'Gold' },
          { key: 'gold_benefits', label: 'Gold Benefits', type: 'textarea' },
        ]
      }
    ]
  },
  contact: {
    name: 'Contact Page',
    sections: [
      {
        id: 'info',
        name: 'Contact Information',
        fields: [
          { key: 'title', label: 'Page Title', type: 'text', placeholder: 'Get in Touch' },
          { key: 'subtitle', label: 'Subtitle', type: 'text', placeholder: "We'd love to hear from you" },
          { key: 'email', label: 'Email', type: 'text', placeholder: 'hello@chaleur.online' },
          { key: 'phone', label: 'Phone', type: 'text', placeholder: '+91 ...' },
          { key: 'address', label: 'Address', type: 'textarea', placeholder: 'Your address...' },
          { key: 'whatsapp', label: 'WhatsApp Number', type: 'text', placeholder: '+91...' },
        ]
      }
    ]
  },
  footer: {
    name: 'Footer',
    sections: [
      {
        id: 'info',
        name: 'Footer Content',
        fields: [
          { key: 'tagline', label: 'Tagline', type: 'text', placeholder: 'Embrace Giving, Embody Ownership' },
          { key: 'copyright', label: 'Copyright Text', type: 'text', placeholder: '© 2024 Chaleur. All rights reserved.' },
          { key: 'email', label: 'Email', type: 'text', placeholder: 'hello@chaleur.online' },
          { key: 'instagram', label: 'Instagram URL', type: 'text', placeholder: 'https://instagram.com/chaleur' },
          { key: 'facebook', label: 'Facebook URL', type: 'text', placeholder: 'https://facebook.com/chaleur' },
          { key: 'pinterest', label: 'Pinterest URL', type: 'text', placeholder: 'https://pinterest.com/chaleur' },
        ]
      }
    ]
  },
  faq: {
    name: 'FAQ Page',
    sections: [
      {
        id: 'header',
        name: 'Page Header',
        fields: [
          { key: 'title', label: 'Page Title', type: 'text', placeholder: 'Frequently Asked Questions' },
          { key: 'subtitle', label: 'Subtitle', type: 'text', placeholder: 'Find answers to common questions' },
        ]
      },
      {
        id: 'questions',
        name: 'FAQ Items',
        fields: [
          { key: 'q1_question', label: 'Question 1', type: 'text' },
          { key: 'q1_answer', label: 'Answer 1', type: 'textarea' },
          { key: 'q2_question', label: 'Question 2', type: 'text' },
          { key: 'q2_answer', label: 'Answer 2', type: 'textarea' },
          { key: 'q3_question', label: 'Question 3', type: 'text' },
          { key: 'q3_answer', label: 'Answer 3', type: 'textarea' },
          { key: 'q4_question', label: 'Question 4', type: 'text' },
          { key: 'q4_answer', label: 'Answer 4', type: 'textarea' },
          { key: 'q5_question', label: 'Question 5', type: 'text' },
          { key: 'q5_answer', label: 'Answer 5', type: 'textarea' },
        ]
      }
    ]
  },
  privacy: {
    name: 'Privacy Policy Page',
    sections: [
      {
        id: 'content',
        name: 'Page Content',
        fields: [
          { key: 'title', label: 'Page Title', type: 'text', placeholder: 'Privacy Policy' },
          { key: 'intro', label: 'Introduction', type: 'textarea' },
          { key: 'data_collection', label: 'Data Collection', type: 'textarea' },
          { key: 'data_usage', label: 'How We Use Data', type: 'textarea' },
          { key: 'cookies', label: 'Cookie Policy', type: 'textarea' },
          { key: 'contact_info', label: 'Contact Information', type: 'textarea' },
        ]
      }
    ]
  },
  refund: {
    name: 'Refund Policy Page',
    sections: [
      {
        id: 'content',
        name: 'Page Content',
        fields: [
          { key: 'title', label: 'Page Title', type: 'text', placeholder: 'Refund & Return Policy' },
          { key: 'intro', label: 'Introduction', type: 'textarea' },
          { key: 'eligibility', label: 'Return Eligibility', type: 'textarea' },
          { key: 'process', label: 'Return Process', type: 'textarea' },
          { key: 'refunds', label: 'Refund Details', type: 'textarea' },
          { key: 'exchanges', label: 'Exchange Policy', type: 'textarea' },
        ]
      }
    ]
  }
};

const AdminCMS = () => {
  const [content, setContent] = useState({});
  const [expandedPages, setExpandedPages] = useState({ homepage: true });
  const [saving, setSaving] = useState({});
  const [loading, setLoading] = useState(true);
  const [imagePickerOpen, setImagePickerOpen] = useState(null); // {pageId, sectionId, fieldKey}
  const token = localStorage.getItem('chaleur_admin_token');

  useEffect(() => {
    fetchAllContent();
  }, []);

  const fetchAllContent = async () => {
    try {
      const response = await axios.get(`${API}/cms`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const pages = response.data.pages || {};
      const formattedContent = {};
      
      Object.entries(pages).forEach(([pageId, sections]) => {
        formattedContent[pageId] = {};
        sections.forEach(section => {
          formattedContent[pageId][section.section_id] = section.content || {};
        });
      });
      
      setContent(formattedContent);
    } catch (error) {
      console.error('Error fetching CMS content:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFieldChange = (pageId, sectionId, fieldKey, value) => {
    setContent(prev => ({
      ...prev,
      [pageId]: {
        ...prev[pageId],
        [sectionId]: {
          ...(prev[pageId]?.[sectionId] || {}),
          [fieldKey]: value
        }
      }
    }));
  };

  const saveSection = async (pageId, sectionId) => {
    const savingKey = `${pageId}-${sectionId}`;
    setSaving(prev => ({ ...prev, [savingKey]: true }));
    
    try {
      const sectionContent = content[pageId]?.[sectionId] || {};
      
      await axios.put(
        `${API}/cms/${pageId}/${sectionId}`,
        sectionContent,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success('Content saved successfully');
    } catch (error) {
      console.error('Error saving content:', error);
      toast.error('Failed to save content');
    } finally {
      setSaving(prev => ({ ...prev, [savingKey]: false }));
    }
  };

  const togglePage = (pageId) => {
    setExpandedPages(prev => ({
      ...prev,
      [pageId]: !prev[pageId]
    }));
  };

  const getFieldValue = (pageId, sectionId, fieldKey) => {
    return content[pageId]?.[sectionId]?.[fieldKey] || '';
  };

  const openImagePicker = (pageId, sectionId, fieldKey) => {
    setImagePickerOpen({ pageId, sectionId, fieldKey });
  };

  const handleImageSelect = (imageUrl) => {
    if (imagePickerOpen) {
      handleFieldChange(imagePickerOpen.pageId, imagePickerOpen.sectionId, imagePickerOpen.fieldKey, imageUrl);
    }
    setImagePickerOpen(null);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="h-8 w-48 bg-gray-100 animate-pulse rounded" />
        {[...Array(3)].map((_, i) => (
          <div key={i} className="h-32 bg-gray-100 animate-pulse rounded" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-cms">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-light mb-2">Content Management</h1>
          <p className="text-muted-foreground">Edit text and images across your website</p>
        </div>
        <Button variant="outline" onClick={fetchAllContent} className="rounded-none">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      <div className="space-y-4">
        {Object.entries(PAGE_STRUCTURE).map(([pageId, page]) => (
          <Card key={pageId} className="rounded-none border-gray-200">
            <CardHeader 
              className="cursor-pointer hover:bg-gray-50 transition-colors"
              onClick={() => togglePage(pageId)}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-primary" />
                  <CardTitle className="text-lg font-medium">{page.name}</CardTitle>
                  <span className="text-sm text-muted-foreground">
                    ({page.sections.length} sections)
                  </span>
                </div>
                {expandedPages[pageId] ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </div>
            </CardHeader>
            
            {expandedPages[pageId] && (
              <CardContent className="space-y-6 pt-0">
                {page.sections.map(section => {
                  const savingKey = `${pageId}-${section.id}`;
                  return (
                    <div key={section.id} className="border border-gray-100 p-6 bg-gray-50/50">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-medium text-sm uppercase tracking-wide text-muted-foreground">
                          {section.name}
                        </h3>
                        <Button
                          size="sm"
                          onClick={() => saveSection(pageId, section.id)}
                          disabled={saving[savingKey]}
                          className="rounded-none"
                        >
                          {saving[savingKey] ? (
                            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          ) : (
                            <Save className="w-4 h-4 mr-2" />
                          )}
                          Save Section
                        </Button>
                      </div>
                      
                      <div className="grid gap-4">
                        {section.fields.map(field => (
                          <div key={field.key} className="space-y-2">
                            <label className="text-sm font-medium flex items-center gap-2">
                              {field.type === 'image' ? (
                                <Image className="w-4 h-4 text-muted-foreground" />
                              ) : field.type === 'textarea' ? (
                                <Type className="w-4 h-4 text-muted-foreground" />
                              ) : (
                                <Type className="w-4 h-4 text-muted-foreground" />
                              )}
                              {field.label}
                            </label>
                            
                            {/* Image field with picker */}
                            {field.type === 'image' ? (
                              <div className="flex gap-3 items-start">
                                <div className="flex-1">
                                  <div className="flex gap-2">
                                    <Input
                                      value={getFieldValue(pageId, section.id, field.key)}
                                      onChange={(e) => handleFieldChange(pageId, section.id, field.key, e.target.value)}
                                      placeholder={field.placeholder || 'Image URL'}
                                      className="rounded-none flex-1"
                                    />
                                    <Button 
                                      type="button" 
                                      variant="outline" 
                                      onClick={() => openImagePicker(pageId, section.id, field.key)}
                                      className="rounded-none"
                                    >
                                      <FolderOpen className="w-4 h-4 mr-2" />
                                      Browse
                                    </Button>
                                  </div>
                                  {/* Image preview */}
                                  {getFieldValue(pageId, section.id, field.key) && (
                                    <div className="mt-2 relative w-32 h-20 bg-gray-100 overflow-hidden group">
                                      <img 
                                        src={getFieldValue(pageId, section.id, field.key)} 
                                        alt="Preview" 
                                        className="w-full h-full object-cover"
                                        onError={(e) => { e.target.style.display = 'none'; }}
                                      />
                                      <button
                                        type="button"
                                        onClick={() => handleFieldChange(pageId, section.id, field.key, '')}
                                        className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                                      >
                                        <X className="w-3 h-3" />
                                      </button>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ) : field.type === 'textarea' ? (
                              <Textarea
                                value={getFieldValue(pageId, section.id, field.key) || ''}
                                onChange={(e) => handleFieldChange(pageId, section.id, field.key, e.target.value)}
                                placeholder={field.placeholder || 'Enter content...'}
                                className="rounded-none min-h-[100px] resize-y"
                              />
                            ) : (
                              <Input
                                value={getFieldValue(pageId, section.id, field.key) || ''}
                                onChange={(e) => handleFieldChange(pageId, section.id, field.key, e.target.value)}
                                placeholder={field.placeholder || 'Enter text...'}
                                className="rounded-none"
                              />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            )}
          </Card>
        ))}
      </div>

      {/* Image Picker Modal */}
      {imagePickerOpen && (
        <CMSImagePicker
          currentImage={getFieldValue(imagePickerOpen.pageId, imagePickerOpen.sectionId, imagePickerOpen.fieldKey)}
          onSelect={handleImageSelect}
          onClose={() => setImagePickerOpen(null)}
          token={token}
        />
      )}
    </div>
  );
};

export default AdminCMS;
